// `no-inline` forces a real emitted, hashed file — Vite's default asset pipeline
// inlines anything this small as a `data:` URI, and `AudioWorklet.addModule()`
// accepting a data: URI is not reliably true across browsers (notably Safari/iOS,
// the platform this feature most needs to work on).
import workletUrl from './pcm16-worklet.js?url&no-inline';

/**
 * Low-level mic capture for the AssemblyAI provider. Zero React, zero knowledge of
 * `SpeechProvider` — it just turns a live microphone into 16kHz PCM16 chunks. The
 * module-scoped "one session at a time" guard lives in `../speechSessionRegistry`,
 * not here, since that guard now has to span both providers.
 */

export type CaptureErrorCode = 'permission-denied' | 'no-microphone' | 'microphone-busy' | 'unsupported';

export class CaptureError extends Error {
  code: CaptureErrorCode;
  cause?: unknown;

  constructor(code: CaptureErrorCode, message: string, cause?: unknown) {
    super(message);
    this.name = 'CaptureError';
    this.code = code;
    this.cause = cause;
  }
}

export function isCaptureSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.isSecureContext &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof AudioWorkletNode !== 'undefined'
  );
}

/**
 * Must be called synchronously inside the click handler that starts capture, with
 * no `await` before it. iOS requires the AudioContext to be constructed inside the
 * user-gesture callback itself — created after an `await`, it's born `suspended`
 * and `resume()` never rejects and never resolves the audio path either: nothing
 * errors, audio just never flows.
 */
export function createAudioContext(): AudioContext {
  return new AudioContext({ sampleRate: 16000, latencyHint: 'interactive' });
}

export interface AudioCapture {
  stop(): void;
}

export async function startCapture(
  ctx: AudioContext,
  onChunk: (pcm: ArrayBuffer) => void,
): Promise<AudioCapture> {
  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
  } catch (err) {
    throw mapGetUserMediaError(err);
  }

  await ctx.audioWorklet.addModule(workletUrl);

  const source = ctx.createMediaStreamSource(stream);
  const worklet = new AudioWorkletNode(ctx, 'pcm16-downsampler');
  // Firefox prunes worklet nodes that never reach ctx.destination, which silently
  // stops process() from being called — no error, transcription just never starts.
  // A zero-gain node keeps the graph "live" without producing audible output.
  const silentGain = ctx.createGain();
  silentGain.gain.value = 0;

  source.connect(worklet);
  worklet.connect(silentGain);
  silentGain.connect(ctx.destination);

  worklet.port.onmessage = (event: MessageEvent<ArrayBuffer>) => {
    onChunk(event.data);
  };

  return {
    stop(): void {
      worklet.port.onmessage = null;
      source.disconnect();
      worklet.disconnect();
      silentGain.disconnect();
      // Stopping the tracks — not closing the AudioContext — is what turns off
      // the browser's microphone-in-use indicator; the caller owns the
      // AudioContext it created and closes it separately.
      stream.getTracks().forEach((track) => track.stop());
    },
  };
}

function mapGetUserMediaError(err: unknown): CaptureError {
  const name = err instanceof DOMException ? err.name : undefined;
  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return new CaptureError('no-microphone', 'No microphone was found on this device.', err);
  }
  if (name === 'NotReadableError' || name === 'AbortError') {
    return new CaptureError('microphone-busy', 'The microphone is in use by another application.', err);
  }
  // NotAllowedError/SecurityError and any other unrecognized rejection default
  // here — in practice this is overwhelmingly a blocked permission prompt.
  return new CaptureError('permission-denied', 'Microphone access was blocked.', err);
}
